import { auth } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';

// Generic CRUD operations
export class FirebaseService {
  // Helper method to check if Firebase is initialized
  private static checkFirebaseInitialized() {
    if (!db) {
      throw new Error('Firebase is not initialized. Please check your configuration.');
    }
    if (!storage) {
      throw new Error('Firebase Storage is not initialized. Please check your configuration.');
    }
  }

  // Helper method to check if user is authenticated
  private static checkAuthentication() {
    console.log('��� Checking authentication...');
    console.log('��� Current user:', auth.currentUser);
    console.log('�� User email:', auth.currentUser?.email);
    console.log('��� User UID:', auth.currentUser?.uid);
    
    if (!auth.currentUser) {
      console.error('❌ No authenticated user found');
      throw new Error('User is not authenticated. Please log in first.');
    }
    console.log('✅ User is authenticated');
  }

  // Create a new document
  static async create(collectionName: string, data: any) {
    try {
      this.checkFirebaseInitialized();
      
      const docRef = doc(collection(db!, collectionName));
      await setDoc(docRef, {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      console.log('✅ Document created:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('❌ Error creating document:', error);
      throw error;
    }
  }

  // Read a single document
  static async read(collectionName: string, docId: string) {
    try {
      this.checkFirebaseInitialized();
      
      const docRef = doc(db!, collectionName, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        console.log('✅ Document found:', docId);
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        console.log('❌ Document not found:', docId);
        return null;
      }
    } catch (error) {
      console.error('❌ Error reading document:', error);
      throw error;
    }
  }

  // Read all documents from a collection
  static async readAll(collectionName: string) {
    try {
      this.checkFirebaseInitialized();
      
      console.log('��� Reading all documents from collection:', collectionName);
      const querySnapshot = await getDocs(collection(db!, collectionName));
      const documents: any[] = [];
      
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });
      
      console.log('✅ Found', documents.length, 'documents in', collectionName);
      console.log('��� Documents:', documents);
      return documents;
    } catch (error) {
      console.error('❌ Error getting documents:', error);
      throw error;
    }
  }

  // Update a document
  static async update(collectionName: string, docId: string, data: any) {
    try {
      this.checkFirebaseInitialized();
      
      const docRef = doc(db!, collectionName, docId);
      await updateDoc(docRef, {
        ...data,
        updatedAt: new Date()
      });
      
      console.log('✅ Document updated:', docId);
      return docId;
    } catch (error) {
      console.error('❌ Error updating document:', error);
      throw error;
    }
  }

  // Delete a document
  static async delete(collectionName: string, docId: string) {
    try {
      this.checkFirebaseInitialized();
      
      const docRef = doc(db!, collectionName, docId);
      await deleteDoc(docRef);
      
      console.log('✅ Document deleted:', docId);
      return docId;
    } catch (error) {
      console.error('❌ Error deleting document:', error);
      throw error;
    }
  }

  // Upload a file to Firebase Storage
  static async uploadFile(path: string, file: File): Promise<string> {
    try {
      this.checkFirebaseInitialized();
      
      console.log('��� Starting upload for:', path);
      console.log('��� File details:', { name: file.name, size: file.size, type: file.type });
      
      const storageRef = ref(storage!, path);
      console.log('��� Storage reference created:', storageRef);
      
      const snapshot = await uploadBytes(storageRef, file);
      console.log('✅ Upload completed:', snapshot.metadata.name);
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('��� Download URL generated:', downloadURL);
      
      return downloadURL;
    } catch (error) {
      console.error('❌ Error uploading file:', error);
      throw error;
    }
  }

  // Delete a file from Firebase Storage
  static async deleteFile(path: string) {
    try {
      this.checkFirebaseInitialized();
      
      const storageRef = ref(storage!, path);
      await deleteObject(storageRef);
      
      console.log('✅ File deleted:', path);
      return path;
    } catch (error) {
      console.error('❌ Error deleting file:', error);
      throw error;
    }
  }

  // Alias methods for compatibility with existing code
  static async getById(collectionName: string, docId: string) {
    return this.read(collectionName, docId);
  }

  static async getAll(collectionName: string) {
    return this.readAll(collectionName);
  }

  // Product-specific methods
  static async createProduct(productData: any) {
    return this.create('products', productData);
  }

  static async getProduct(productId: string) {
    return this.read('products', productId);
  }

  static async getAllProducts() {
    return this.readAll('products');
  }

  static async updateProduct(productId: string, productData: any) {
    return this.update('products', productId, productData);
  }

  static async deleteProduct(productId: string) {
    return this.delete('products', productId);
  }

  static async uploadProductImage(file: File, productId?: string) {
    const timestamp = Date.now();
    const fileName = productId 
      ? `${productId}_${timestamp}_${file.name}`
      : `${timestamp}_${file.name}`;
    const path = `products/${fileName}`;
    
    return this.uploadFile(path, file);
  }

  // Generic image upload method with progress callback
  static async uploadImage(file: File, onProgress?: (progress: number) => void): Promise<string> {
    try {
      this.checkFirebaseInitialized();
      
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const path = `images/${fileName}`;
      
      const storageRef = ref(storage!, path);
      
      // Upload file with progress tracking
      const uploadTask = uploadBytes(storageRef, file);
      
      // Track upload progress if callback provided
      if (onProgress) {
        // For Firebase v9, we can't track progress with uploadBytes
        // We'll just call the progress callback with 100% when complete
        uploadTask.then(() => {
          onProgress(100);
        });
      }
      
      const snapshot = await uploadTask;
      console.log('✅ Upload completed:', snapshot.metadata.name);
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('🔗 Download URL generated:', downloadURL);
      
      return downloadURL;
    } catch (error) {
      console.error('❌ Error uploading image:', error);
      throw error;
    }
  }
}
